/*
 *
 *  * Copyright(c) 2021 General Electric Company. All rights reserved.
 *  *
 *  * The copyright to the computer software herein is the property of
 *  * General Electric Company. The software may be used and/or copied only
 *  * with the written permission of General Electric Company or in accordance
 *  * with the terms and conditions stipulated in the agreement/contract
 *  * under which the software has been supplied.
 *
 */

import * as _ from 'underscore';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { AppConfigService } from '@app/common/app-config.service';
import { ModalConfig, ModalRef } from '@wfm/core';
import { NotifyService } from '@app/common/notify.service';
import { TranslateService } from '@ngx-translate/core';
import { EventMessage, Events, EventService } from '@app/common/event';
import { AuditService } from '@app/common/audit.service';
import { EventWorkItem } from '@app/data-model/event-work-item';
import { Logger } from '@core/lib/services';
import { UserNamePipe } from '@app/common/pipes';
import { UtilService } from '@app/util/util.service';
import { SearchFilterCriteriaService } from '@app/ui/search/search-filter-criteria.service';
import { NativeAcademicWorkflowService } from '@app/di/native-academic-workflow.service';

const log = new Logger('AssignExamComponent');

@Component({
  selector: 'app-assign-exam',
  templateUrl: './assign-exam.component.html',
  styleUrls: ['./assign-exam.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UserNamePipe]
})
export class AssignExamComponent implements OnInit {
  private examsActivated = 0;
  private selectApproverExams = 0;
  private batchUpdateCompleted = false;
  private batchRequestSize = 10;

  loading: boolean;
  radList;
  selected;
  currentUser;
  currentUserName;
  isRadListPrivileged;
  assignActionCode: string;
  isNawExam: boolean;
  examInstituteId: number;

  constructor(
    public config: ModalConfig,
    public modalRef: ModalRef,
    private cd: ChangeDetectorRef,
    private notify: NotifyService,
    private eventService: EventService,
    private translate: TranslateService,
    private userNamePipe: UserNamePipe,
    private auditService: AuditService,
    private appConfigService: AppConfigService,
    private searchFilterCriteriaService: SearchFilterCriteriaService,
    private nativeAcademicWorkflowService: NativeAcademicWorkflowService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.currentUserName = this.appConfigService.getUserId();

    this.isRadListPrivileged = this.selectedConfidentialExam(this.config.data.item);

    this.isNawExam = this.nativeAcademicWorkflowService.containsNawExam(this.config.data.item);
    this.examInstituteId = this.appConfigService.isInstituteSeparationEnabled()
      ? this.config.data.item.instituteId
      : undefined;

    this.searchFilterCriteriaService
      .loadAssignees(this.isRadListPrivileged, this.examInstituteId)
      .then(
        () => {
          const radList = this.searchFilterCriteriaService.getAssignee(this.isRadListPrivileged, this.examInstituteId);
          for (let i = 0; i < radList.length; i++) {
            if (this.currentUserName === radList[i].userName) {
              this.currentUser = radList[i];
              radList.splice(i, 1);
              break;
            }
          }
          this.radList = radList;
        },
        error => {
          if (error !== 'timeout') {
            this.notify.error(error);
          }
        }
      )
      .finally(() => {
        this.loading = false;
        this.cd.detectChanges();
      });
  }

  assign() {
    if (!this.selected.userName) {
      this.notify.error(this.translate.instant('assignments.error.selectAnAssignee'));
      return;
    }
    const item = this.config.data.item;

    if (item.state) {
      if (UtilService.isBeingRead(item)) {
        const errorMessage = [
          this.translate.instant('assignments.error.examNotAssign1'),
          this.userNamePipe.transform(item.state.activity.readingDto),
          this.translate.instant('assignments.error.examNotAssign2')
        ].join(' ');
        this.notify.error(errorMessage);
        return;
      }

      // Attempt to re-assign to same user
      if (
        item.state.isAssigned &&
        !item.state.ownership.assignedToUser[0].externalUser &&
        item.state.ownership.assignedToUser[0].userName === this.selected.userName
      ) {
        return;
      }

      // If exam is in assigned state, unassign exam prior to assign
      if (item.state.isAssigned) {
        this.assignActionCode = this.auditService.getActionCode().Update;
        this.eventService
          .sendEvent(
            new EventMessage(Events.unassign, new EventWorkItem(item.id), {
              userName: item.state.ownership.assignedToUser[0].userName
            })
          )
          .then(
            () => {
              log.info('unassign action executed successfully');
            },
            () => {
              log.info('error during unassign action');
            }
          )
          .finally(() => {
            this.assignExam();
          });
      } else {
        this.assignExam();
      }
    } else {
      this.assignExams();
    }
  }

  private generateAuditParticipants(exam) {
    const examAuditInfo = this.auditService.getExamAuditInfo(exam);
    return examAuditInfo.participants;
  }

  private assignExam() {
    const item = this.config.data.item;
    const params = {
      id: item.id
    };
    const auditData: any = this.auditService.getExamAuditInfo(item) ? this.auditService.getExamAuditInfo(item) : {};
    this.loading = true;
    // we need to add the selected user name  to the audit information
    auditData.ownerName = this.selected.userName;

    this.eventService
      .sendEvent(
        new EventMessage(Events.assign, new EventWorkItem(item.id, params), {
          userName: this.selected.userName,
          examAudit: auditData
        })
      )
      .catch(() => {
        this.notify.error(this.translate.instant('assignments.error.examFailedToAssign'));
      })
      .finally(() => {
        this.loading = false;
      });
  }

  private assignExams() {
    const userName = this.selected.userName;
    let promise;
    const auditParticipants = [];
    let auditData;
    const eligibleExams = _.pluck(
      _.filter(this.config.data.item, exam => {
        const participant = this.generateAuditParticipants(exam);
        //participant is an array , the first value is for the exam Id and the second is for patient participant
        // we push them to the participants list that will be sent to the audit service
        auditParticipants.push(participant[0], participant[1]);
        return exam;
      }),
      'id'
    );
    if (eligibleExams.length > this.batchRequestSize) {
      let batchData = eligibleExams.splice(0, this.batchRequestSize);
      let auditBatchData = auditParticipants.splice(0, 20);
      while (!_.isEmpty(batchData)) {
        auditData = {};
        auditData.participants = auditBatchData;
        promise = this.eventService.sendEvent(
          new EventMessage(Events.assignAll, batchData, { userName: userName, examAudit: auditData })
        );
        promise && promise.then(this.handleResponse, this.handleError);
        batchData = eligibleExams.splice(0, this.batchRequestSize);
        auditBatchData = auditParticipants.splice(0, 20);
      }
    } else {
      //Send a single request
      auditData = {};
      auditData.participants = auditParticipants;
      this.batchUpdateCompleted = true;
      promise = this.eventService.sendEvent(
        new EventMessage(Events.assignAll, eligibleExams, { userName: userName, examAudit: auditData })
      );
      promise && promise.then(this.handleResponse, this.handleError);
    }
  }

  private selectedConfidentialExam(item: any): boolean {
    //Multiple Exams are selected
    if (Array.isArray(item)) {
      //Is there a confidential exam present in the array
      return !_.isEmpty(_.findWhere(item, { patientVipCode: 'Y' }));
    } else {
      //Single exam selected. Is it confidential patient?
      return item.patientVipCode === 'Y';
    }
  }

  private handleResponse = (data?: any) => {
    const examsActivated = _.filter(data, exam => {
      return exam.activity && exam.activity.readingDto && exam.activity.readingDto.userName !== this.currentUserName;
    });
    this.examsActivated += examsActivated.length;
    const selectApproverExams = _.filter(data, exam => {
      return _.contains(['RA1000_Approver_Added'], exam.extAppWorkItemState);
    });
    this.selectApproverExams += selectApproverExams.length;
    if (this.batchUpdateCompleted) {
      let message;
      if (this.examsActivated > 0) {
        message = examsActivated.length + this.translate.instant('assignments.error.alreadyActivatedExamFailed');
      }
      if (this.selectApproverExams > 0) {
        const ra1000Message =
          this.translate.instant('assignments.error.canNotReassignRA1000For') +
          selectApproverExams.length +
          this.translate.instant('takeControl.examsLabelDot');
        if (message) {
          message = message + '\n' + ra1000Message;
        } else {
          message = ra1000Message;
        }
      }
      if (message) {
        this.notify.error(message);
      }
    }
  };
  private handleError = () => {
    this.handleResponse();
  };
}
